// EditAliasDialog — set or clear a room's optional alias (custom name)
// during capture (FFR2).
//
// Lets a surveyor rename ANY existing room — including admin-seeded
// template rooms — to a friendlier navigation label, or clear it back to
// the generated displayName. The alias supplements (never replaces) the
// canonical displayName and is navigation-only (NOT in the CSV export).
//
// The input is prefilled with the room's current alias and capped at 255
// (matching spaces.alias varchar(255) + the capture service's cap, so the
// field can't accept a value the backend would reject). Confirming with an
// empty / whitespace-only value CLEARS the alias (sends null); confirming
// with text SETS it. The caller wires confirm to the optimistic update +
// updateSpaceAlias dispatch.
//
// Layered on the shared `<Dialog>` primitive (focus trap, Esc-to-close,
// overlay dismiss, return-focus-to-invoker), same as DeleteRoomDialog.

import { useState, type ReactElement } from 'react'

import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { Dialog } from '../../ui/dialog'
import { FormField } from '../../ui/form-field'
import { TextInput } from '../../ui/text-input'

// Matches spaces.alias varchar(255) + the capture service's 255-char cap.
const ALIAS_MAX_LENGTH = 255

export interface EditAliasDialogProps {
  open: boolean
  /** Canonical generated name of the room (e.g. "Office 3"), shown as context. */
  roomName: string
  /** The room's current alias (null when it has none). Prefills the input. */
  currentAlias: string | null
  /** True while the alias mutation is in flight. */
  submitting: boolean
  /** Error message to surface; null hides the alert row. */
  error: string | null
  /** Called when the user dismisses without saving (Cancel/Esc/overlay). */
  onCancel: () => void
  /**
   * Called with the new alias when the user confirms. A blank / whitespace-
   * only entry yields null (clear); otherwise the trimmed string. The
   * caller applies the same normalisation server-side defensively.
   */
  onConfirm: (alias: string | null) => void
}

export function EditAliasDialog({
  open,
  roomName,
  currentAlias,
  submitting,
  error,
  onCancel,
  onConfirm,
}: EditAliasDialogProps): ReactElement {
  // Seed the field from the current alias each time the dialog opens.
  // `key`-ing the dialog on the room id at the call site remounts this
  // component per room, so the initial state is correct without a
  // cascading set-state-in-effect (React 19's set-state-in-effect rule).
  const [value, setValue] = useState(currentAlias ?? '')

  const trimmed = value.trim()
  // Normalise blank/whitespace → null (clear); otherwise the trimmed name.
  const nextAlias = trimmed.length > 0 ? trimmed : null

  const handleConfirm = (): void => {
    if (submitting) return
    onConfirm(nextAlias)
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={`Rename ${roomName}`}
      description="Set a custom name for this room, or clear it to use the automatic name. This name is for navigation only — it doesn't appear in the export."
      dismissOnOverlayClick={!submitting}
    >
      <div className="flex flex-col gap-4" data-testid="edit-alias-dialog">
        {error !== null ? (
          <Alert variant="error" title="Could not rename room">
            {error}
          </Alert>
        ) : null}

        <FormField
          label="Room name"
          htmlFor="edit-room-alias"
          description={`Leave blank to use the automatic name (${roomName}).`}
        >
          <TextInput
            id="edit-room-alias"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={submitting}
            maxLength={ALIAS_MAX_LENGTH}
            autoComplete="off"
            placeholder={roomName}
            data-testid="edit-alias-input"
          />
        </FormField>

        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Cancel rename"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={submitting}
            loading={submitting}
            aria-label="Save room name"
            data-testid="confirm-edit-alias-btn"
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
